import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useState } from "react";
import { currency } from "utils";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import LinkComponent from "@Components/Link";
import CartLineItem from "@Components/cartLineItem/CartLineItem";
import { FLYOUT_TIME_CA, FLYOUT_TIME_US } from "@Constants/common";
import { convertToDollar } from "@Utils/currency";
import { isCanadianLocale } from "@Utils/locale";
import { ICartLineItem } from "@wff/interfaces";
import { cartToastActions, cartToastSelector } from "@wff/store/cartToastSlice";
import { localeSelector } from "@wff/store/localeSlice";
import { cartSliceSelector, cartQuantitySelector } from "store/cartSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import cartClasses from "../../cart/Cart.module.scss";
import { KEYS_CART } from "../cart-drawer/translation-keys";
import classes from "./CartToast.module.scss";

export const CART_TOAST_ID = "cart-toast";

interface CartToastProps {
  isDeliveryAvailable: boolean;
  isStickyNavVisible: boolean;
}

export default function CartToast({
  isStickyNavVisible,
  isDeliveryAvailable,
}: CartToastProps) {
  const { items } = useAppSelector(cartToastSelector);
  const cart = useAppSelector(cartSliceSelector);
  const totalItemCount: number = useAppSelector(cartQuantitySelector);
  const locale = useAppSelector(localeSelector);
  const { open } = useAppSelector(cartToastSelector);
  const dispatch = useAppDispatch();
  const { t } = useTranslation("common");
  const onClose = useCallback(
    () => dispatch(cartToastActions.setOpenToast(false)),
    [dispatch]
  );
  const mobile = useMediaQuery("(max-width:60em)");
  const [prevPath, setPrevPath] = useState("");
  const route = useRouter().pathname;
  const orderMinimumAmount = convertToDollar(
    cart?.shippingEligibility?.eligibilityThreshold
  );
  const amountToReachOrderMinimum = convertToDollar(
    cart?.shippingEligibility?.eligibilityAmountRemaining
  );
  const showOrderMinimumUnmetSection =
    !Number.isNaN(Number(amountToReachOrderMinimum)) &&
    Number(amountToReachOrderMinimum) > 0;

  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const isCanadaUser = isCanadianLocale(currentLocale);
  const flyOutTime = isCanadaUser ? FLYOUT_TIME_CA : FLYOUT_TIME_US;
  const cartFlyoutNavClass = isStickyNavVisible
    ? classes["cart-flyout__nav-shown"]
    : classes["cart-flyout__nav-hidden"];

  useEffect(() => {
    const t = setTimeout(() => {
      onClose();
    }, flyOutTime);

    return () => {
      clearTimeout(t);
    };
  }, [onClose, open, flyOutTime]);

  useEffect(() => {
    dispatch(cartToastActions.setOpenToast(true));
  }, [items, dispatch]);

  useEffect(() => {
    if (route !== prevPath) onClose();
    setPrevPath(route);
  }, [route, prevPath, onClose]);
  return (
    <Drawer
      anchor={"top"}
      data-testid={CART_TOAST_ID}
      open={open}
      onClose={onClose}
      className={classes["MuiDrawer-paper"]}
      PaperProps={{
        sx: {
          zIndex: "1 !important",
          top: 235,
          width: mobile ? "100%" : "360px",
          left: "unset",
        },
      }}
      BackdropProps={{ style: { backgroundColor: "unset" } }}
      ModalProps={{ onBackdropClick: () => onClose }}
    >
      <div className={classes["cart-flyout"] + " " + cartFlyoutNavClass}>
        <div className={classes["cart-flyout__content"]}>
          <h2 className={classes["toast__title"]}>Added to your cart</h2>
          <div className={classes["cart-flyout__content__items"]}>
            <div className={classes["cart-flyout__content__items-inner"]}>
              {items?.map((cartItem: ICartLineItem, index: number) => {
                return (
                  <CartLineItem
                    key={index}
                    item={cartItem}
                    isFlyOut={false}
                    isToast={true}
                    isReadOnly={true}
                  />
                );
              })}
            </div>
          </div>
          <div className={classes["cart-flyout__footer"]}>
            <div className={classes["cart-flyout__footer-container"]}>
              <LinkComponent href={`/${locale.languageCode}/cart`}>
                <a
                  className={`${classes["cart-flyout__footer-button"]} ${classes.action}`}
                >
                  {t(KEYS_CART.general.view_basket)}
                </a>
              </LinkComponent>
            </div>
          </div>
          <div className={classes["cart-flyout__shipping"]}>
            {totalItemCount > 0 && (
              <div className={cartClasses["cart-content-delivery"]}>
                {showOrderMinimumUnmetSection ? (
                  <p className={cartClasses["cart-content-delivery-amount"]}>
                    {t("checkout.minimum_unmet.add_text")}
                    <span className="red--text">
                      {currency.format(amountToReachOrderMinimum)}
                    </span>
                    <span>{t("checkout.minimum_unmet.minimum_text")}</span>
                    <span className="red--text">
                      {currency.format(orderMinimumAmount)}
                    </span>
                  </p>
                ) : isDeliveryAvailable ? (
                  <p
                    data-testid="cart-content-delivery-message-id"
                    className={cartClasses["cart-content-delivery-message"]}
                  >
                    {t("cart.ship.free_delivery")}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
